import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaHome, FaMoneyBillWave, FaCalculator, FaInfoCircle, FaCheckCircle, FaQuestionCircle, FaChartBar, FaLightbulb, FaQuestion } from 'react-icons/fa';
import Chart from 'react-apexcharts';

const EstateTaxCalculator = () => {
  const [formData, setFormData] = useState({
    grossEstateValue: '',
    debts: '',
    funeralExpenses: '',
    charitableDonations: '',
    maritalDeduction: '',
    taxRate: '40'
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const calculateTax = (e) => {
    e.preventDefault();
    
    const { grossEstateValue, debts, funeralExpenses, charitableDonations, maritalDeduction, taxRate } = formData;
    
    if (!grossEstateValue || !debts || !funeralExpenses || !charitableDonations || !maritalDeduction) {
      setError('Please fill in all required fields');
      return;
    }

    // Calculate taxable estate
    const taxableEstate = parseFloat(grossEstateValue) - 
      (parseFloat(debts) + parseFloat(funeralExpenses) + parseFloat(charitableDonations) + parseFloat(maritalDeduction));

    // Apply tax rate
    const taxAmount = (taxableEstate * parseFloat(taxRate)) / 100;
    const netEstate = taxableEstate - taxAmount;

    setResults({
      taxableEstate: taxableEstate.toFixed(2),
      taxAmount: taxAmount.toFixed(2),
      netEstate: netEstate.toFixed(2)
    });
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(value);
  };

  const series = [
    {
      name: 'Gross Estate',
      data: [parseFloat(formData.grossEstateValue) || 0]
    },
    {
      name: 'Deductions',
      data: [
        (parseFloat(formData.debts) || 0) +
        (parseFloat(formData.funeralExpenses) || 0) +
        (parseFloat(formData.charitableDonations) || 0) +
        (parseFloat(formData.maritalDeduction) || 0)
      ]
    },
    {
      name: 'Taxable Estate',
      data: [results ? parseFloat(results.taxableEstate) : 0]
    },
    {
      name: 'Estate Tax',
      data: [results ? parseFloat(results.taxAmount) : 0]
    },
    {
      name: 'Net Estate',
      data: [results ? parseFloat(results.netEstate) : 0]
    }
  ];

  return (
    <div className="calculator-container bg-gray-100 p-4">
      <Helmet>
        <title>Estate Tax Calculator</title>
        <meta name="description" content="Calculate your estate tax with our easy-to-use calculator." />
      </Helmet>

      <div className="calculator-header text-center mb-8">
        <FaHome className="calculator-icon text-4xl text-green-500" />
        <h1 className="text-3xl font-bold">Estate Tax Calculator</h1>
      </div>

      <div className="calculator-content max-w-3xl mx-auto bg-white p-8 rounded-lg shadow-lg">
        <div className="calculator-form-section">
          <form onSubmit={calculateTax} className="calculator-form">
            <div className="input-group mb-4">
              <label htmlFor="grossEstateValue" className="block text-gray-700 font-bold mb-2">
                <FaMoneyBillWave className="inline-block mr-2" /> Gross Estate Value
              </label>
              <input
                type="number"
                id="grossEstateValue"
                name="grossEstateValue"
                value={formData.grossEstateValue}
                onChange={handleChange}
                placeholder="Enter total value of estate"
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:border-blue-500"
              />
              <span className="info-icon">
                <FaInfoCircle title="Total value of all assets in the estate" className="text-gray-500 hover:text-blue-500" />
              </span>
            </div>

            <div className="input-group mb-4">
              <label htmlFor="debts" className="block text-gray-700 font-bold mb-2">
                <FaCalculator className="inline-block mr-2" /> Debts & Liabilities
              </label>
              <input
                type="number"
                id="debts"
                name="debts"
                value={formData.debts}
                onChange={handleChange}
                placeholder="Enter total debts and liabilities"
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:border-blue-500"
              />
              <span className="info-icon">
                <FaInfoCircle title="Total amount of debts and liabilities" className="text-gray-500 hover:text-blue-500" />
              </span>
            </div>

            <div className="input-group mb-4">
              <label htmlFor="funeralExpenses" className="block text-gray-700 font-bold mb-2">
                <FaCheckCircle className="inline-block mr-2" /> Funeral Expenses
              </label>
              <input
                type="number"
                id="funeralExpenses"
                name="funeralExpenses"
                value={formData.funeralExpenses}
                onChange={handleChange}
                placeholder="Enter funeral and administrative expenses"
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:border-blue-500"
              />
              <span className="info-icon">
                <FaInfoCircle title="Costs related to funeral and estate administration" className="text-gray-500 hover:text-blue-500" />
              </span>
            </div>

            <div className="input-group mb-4">
              <label htmlFor="charitableDonations" className="block text-gray-700 font-bold mb-2">
                <FaQuestionCircle className="inline-block mr-2" /> Charitable Donations
              </label>
              <input
                type="number"
                id="charitableDonations"
                name="charitableDonations"
                value={formData.charitableDonations}
                onChange={handleChange}
                placeholder="Enter charitable donations"
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:border-blue-500"
              />
              <span className="info-icon">
                <FaInfoCircle title="Donations made to qualified charities" className="text-gray-500 hover:text-blue-500" />
              </span>
            </div>

            <div className="input-group mb-4">
              <label htmlFor="maritalDeduction" className="block text-gray-700 font-bold mb-2">
                <FaCalculator className="inline-block mr-2" /> Marital Deduction
              </label>
              <input
                type="number"
                id="maritalDeduction"
                name="maritalDeduction"
                value={formData.maritalDeduction}
                onChange={handleChange}
                placeholder="Enter marital deduction amount"
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:border-blue-500"
              />
              <span className="info-icon">
                <FaInfoCircle title="Amount transferred to a surviving spouse" className="text-gray-500 hover:text-blue-500" />
              </span>
            </div>

            <div className="input-group mb-4">
              <label htmlFor="taxRate" className="block text-gray-700 font-bold mb-2">
                <FaQuestionCircle className="inline-block mr-2" /> Tax Rate (%)
              </label>
              <input
                type="number"
                id="taxRate"
                name="taxRate"
                value={formData.taxRate}
                onChange={handleChange}
                placeholder="Enter tax rate"
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:border-blue-500"
              />
              <span className="info-icon">
                <FaInfoCircle title="Applicable estate tax rate as a percentage" className="text-gray-500 hover:text-blue-500" />
              </span>
            </div>

            <button type="submit" className="calculate-button bg-blue-500 text-white rounded-md py-2 px-4 hover:bg-blue-600 focus:outline-none w-full">
              Calculate Estate Tax
            </button>

            {error && <div className="error-message text-red-500 mt-4">{error}</div>}
          </form>
        </div>

        {results && (
          <div className="results-section mt-8">
            <div className="results-header flex items-center justify-between">
              <h2 className="text-2xl font-bold">Results</h2>
              <FaChartBar className="results-icon text-3xl text-green-500" />
            </div>
            
            <div className="results-chart mt-4">
              <Chart
                options={{
                  chart: {
                    type: 'bar',
                    height: 350,
                    toolbar: {
                      show: false
                    }
                  },
                  plotOptions: {
                    bar: {
                      horizontal: false,
                      columnWidth: '55%',
                      endingShape: 'rounded'
                    },
                  },
                  dataLabels: {
                    enabled: false
                  },
                  stroke: {
                    show: true,
                    width: 2,
                    colors: ['transparent']
                  },
                  xaxis: {
                    categories: ['Values'],
                    labels: {
                      show: false
                    }
                  },
                  yaxis: {
                    labels: {
                      formatter: (value) => formatCurrency(value)
                    }
                  },
                  fill: {
                    opacity: 1
                  },
                  legend: {
                    show: true,
                    position: 'top',
                    horizontalAlign: 'center',
                    offsetX: 0,
                    offsetY: 0
                  }
                }}
                series={series}
                type="bar"
                height={350}
              />
            </div>

            <div className="results-details mt-4">
              <div className="result-item flex items-center justify-between">
                <div className="flex items-center">
                  <FaMoneyBillWave className="mr-2" /> Taxable Estate
                </div>
                <div>{formatCurrency(parseFloat(results.taxableEstate))}</div>
              </div>
              <div className="result-item flex items-center justify-between">
                <div className="flex items-center">
                  <FaCalculator className="mr-2" /> Estate Tax
                </div>
                <div>{formatCurrency(parseFloat(results.taxAmount))}</div>
              </div>
              <div className="result-item flex items-center justify-between">
                <div className="flex items-center">
                  <FaCheckCircle className="mr-2" /> Net Estate
                </div>
                <div>{formatCurrency(parseFloat(results.netEstate))}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="calculator-features mt-8 max-w-3xl mx-auto bg-white p-8 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold mb-4">Key Features & Benefits</h2>
        <div className="features-grid grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="feature-item">
            <FaLightbulb className="feature-icon text-3xl text-yellow-500 mb-2" />
            <h3 className="text-lg font-bold">Comprehensive Calculations</h3>
            <p>Calculate estate tax with all major deductions included.</p>
          </div>
          <div className="feature-item">
            <FaChartBar className="feature-icon text-3xl text-green-500 mb-2" />
            <h3 className="text-lg font-bold">Clear Results</h3>
            <p>See a complete breakdown of your estate value and tax obligations.</p>
          </div>
          <div className="feature-item">
            <FaCheckCircle className="feature-icon text-3xl text-blue-500 mb-2" />
            <h3 className="text-lg font-bold">Planning Tool</h3>
            <p>Helps in estate planning by showing potential tax liabilities.</p>
          </div>
        </div>
      </div>

      <div className="how-to-use mt-8 max-w-3xl mx-auto bg-white p-8 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold mb-4">How to Use</h2>
        <ol className="usage-steps list-decimal pl-5">
          <li>Enter the total value of your estate</li>
          <li>Input total debts and liabilities</li>
          <li>Add funeral and administrative expenses</li>
          <li>Enter charitable donations</li>
          <li>Input marital deduction amount</li>
          <li>Click "Calculate Estate Tax" to see your results</li>
        </ol>
      </div>

      <div className="faq-section mt-8 max-w-3xl mx-auto bg-white p-8 rounded-lg shadow-lg">
        <h2 className="text-2xl font-bold mb-4">Frequently Asked Questions</h2>
        <div className="faq-grid grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="faq-item">
            <FaQuestion className="faq-icon text-3xl text-gray-500 mb-2" />
            <h3 className="text-lg font-bold">What is estate tax?</h3>
            <p>Estate tax is a tax on the transfer of property when a person dies.</p>
          </div>
          <div className="faq-item">
            <FaQuestion className="faq-icon text-3xl text-gray-500 mb-2" />
            <h3 className="text-lg font-bold">What is taxable estate?</h3>
            <p>Taxable estate is the total value of all assets minus allowable deductions.</p>
          </div>
          <div className="faq-item">
            <FaQuestion className="faq-icon text-3xl text-gray-500 mb-2" />
            <h3 className="text-lg font-bold">What are deductions?</h3>
            <p>Deductions include debts, funeral expenses, charitable donations, and marital transfers.</p>
          </div>
          <div className="faq-item">
            <FaQuestion className="faq-icon text-3xl text-gray-500 mb-2" />
            <h3 className="text-lg font-bold">How is estate tax calculated?</h3>
            <p>Estate tax is calculated by applying the tax rate to the taxable estate value.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EstateTaxCalculator;